import { Stack } from '@mui/material';

import Meta from '@/components/Meta';
import { PageContent } from '@/components/styled/page';
import { useIsApplicationsAndDataEnabled } from '@/hooks/useIsApplicationsAndDataEnabled';
import useIsNewTrainingUIEnabled from '@/hooks/useIsNewTrainingUIEnabled';
import useIsOrgNavBar2025Enabled from '@/hooks/useIsOrgNavBar2025Enabled';
import Footer from '@/sections/Footer';
import GlobalHeader from '@/sections/GlobalHeader';
import asyncComponentLoader from '@/utils/react/loader';

import Chains from './Chains';
import ModelAPIs from './ModelAPIs';
import Models from './Models';
import Overview from './Overview';
import Sidebar from './Sidebar';
import Training from './Training';
import TrainingLegacy from './Training_Legacy';
import { useWorkspaceSection } from './hooks';
import Navigation from './sections-legacy/Navigation';
import { WorkspaceSections } from './types';
import { mapSectionToTitle } from './utils';

const workspaceSectionComponents = {
  [WorkspaceSections.Applications]: asyncComponentLoader(() => import('@/legacy/Applications')),
  [WorkspaceSections.Data]: asyncComponentLoader(() => import('@/legacy/Data')),
  [WorkspaceSections.Models]: asyncComponentLoader(() => import('./sections-legacy/Models')),

  // new navigation
  [WorkspaceSections.ModelAPIs]: ModelAPIs,
  [WorkspaceSections.Chains]: Chains,
  [WorkspaceSections.Training]: TrainingLegacy,
  [WorkspaceSections.Overview]: Overview,
};

function getSectionComponents(isOrgNavBar2025Enabled: boolean, isNewTrainingUIEnabled: boolean) {
  let components = workspaceSectionComponents;

  if (isOrgNavBar2025Enabled) {
    components = {
      ...components,
      [WorkspaceSections.Models]: Models,
    };

    if (isNewTrainingUIEnabled) {
      components = {
        ...components,
        [WorkspaceSections.Training]: Training,
      };
    }
  }

  return components;
}

function Workspace() {
  const section = useWorkspaceSection();
  const isApplicationsAndDataEnabled = useIsApplicationsAndDataEnabled();
  const isOrgNavBar2025Enabled = useIsOrgNavBar2025Enabled();
  const isNewTrainingUIEnabled = useIsNewTrainingUIEnabled();

  const sectionComponents = getSectionComponents(isOrgNavBar2025Enabled, isNewTrainingUIEnabled);

  function getContent() {
    const SectionComponent = sectionComponents[section];

    if (isOrgNavBar2025Enabled) {
      return (
        <Stack direction="row" overflow="hidden" height="100%" flex={1}>
          <Sidebar selectedSection={section} />
          <Stack overflow="auto" flex={1} height="100%">
            <SectionComponent />
          </Stack>
        </Stack>
      );
    }

    return (
      <PageContent>
        <Stack sx={{ flex: 1 }}>
          <SectionComponent />
        </Stack>
        <Footer />
      </PageContent>
    );
  }

  return (
    // we will remove the type assertion once we remove the legacy navigation
    <Meta title={`${mapSectionToTitle[section as keyof typeof mapSectionToTitle]} | Baseten`}>
      <GlobalHeader />
      {isApplicationsAndDataEnabled && !isOrgNavBar2025Enabled && <Navigation section={section} />}
      {getContent()}
    </Meta>
  );
}

export default Workspace;
